import { Fragment, useState } from 'react'
import { Delete, FiberManualRecord } from '@mui/icons-material'
import {
  ListItem,
  Tooltip,
  IconButton,
  CircularProgress,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material'
import { useDeleteDocumentFlag } from '@/service-library/hooks/document-flags'
import { useDeleteRowValueFlag } from '@/service-library/hooks/row-value-flags'
import queryKeys from '@/service-library/query-keys'
import useIsSuperUserVisible from '@/services/hooks/useIsSuperUserVisible'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { DocumentFlag, RowValueFlag } from '@/types/flags'
import { focusOnField } from '@/utils/row-values'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import FlagLongDescription from './FlagLongDescription'
import useFlagColorMap from './useFlagColorMap'

type FlagListItemBaseProps = {
  flag: DocumentFlag | RowValueFlag
  dismissFlag: () => void
  isDismissing: boolean
  onClick?: () => void
}

function FlagListItemBase({
  flag,
  onClick,
  dismissFlag,
  isDismissing,
}: FlagListItemBaseProps) {
  const isSuperUserVisible = useIsSuperUserVisible()
  const colorMap = useFlagColorMap()
  const color = colorMap[flag.flag_level]
  const ListItemWrapper = onClick ? ListItemButton : Fragment
  // This is stupid, but it prevents an error from being shown in the console from passing onClick to a Fragment,
  // even though its undefined 🤦‍♂️
  const listItemWrapperProps = onClick ? { onClick } : {}
  const isDismissible =
    flag.flag_level !== 'error' && flag.flag_level !== 'failure'
  return (
    <ListItem
      dense
      disablePadding={!!onClick}
      secondaryAction={
        isDismissible || isSuperUserVisible ? (
          !isDismissing ? (
            <Tooltip title="Dismiss">
              <IconButton
                edge="end"
                size="small"
                sx={{ transform: 'translateX(4px)' }} // Slight adjustment for positioning
                onClick={() => dismissFlag()}
              >
                <Delete
                  fontSize="small"
                  sx={{
                    color: isDismissible ? undefined : SUPER_USER_ONLY_COLOR,
                  }}
                />
              </IconButton>
            </Tooltip>
          ) : (
            <Stack direction="row" alignItems="center">
              <CircularProgress size={16} color="neutral" />
            </Stack>
          )
        ) : undefined
      }
    >
      <ListItemWrapper {...listItemWrapperProps}>
        <ListItemIcon sx={{ minWidth: 28 }}>
          <FiberManualRecord sx={{ fontSize: 16, color: color.base }} />
        </ListItemIcon>
        <ListItemText
          sx={{ color: color.base }}
          primary={flag.short_description || flag.flag_type?.short_description}
          secondary={
            <FlagLongDescription longDescription={flag.long_description} />
          }
          slotProps={{
            secondary: {
              component: 'div',
            },
          }}
        />
      </ListItemWrapper>
    </ListItem>
  )
}

type DocumentFlagListItemProps = {
  flag: DocumentFlag
}

type RowValueFlagListItemProps = {
  flag: RowValueFlag
  onFlagClick?: (
    flag: RowValueFlag,
    cellToFocus: HTMLInputElement | HTMLDivElement | null,
  ) => void
}

export const flagDeleteMutateOptions = {
  onSuccess: () => {
    showSuccessSnackbar('Flag Dismissed')
  },
  onError: () => {
    showErrorSnackbar('Error Dismissing Flag. Please try again later.')
  },
}

export function DocumentFlagListItem({ flag }: DocumentFlagListItemProps) {
  const [isDismissing, setIsDismissing] = useState(false)
  const { deleteDocumentFlag } = useDeleteDocumentFlag({
    sideEffectQueryKeys: [
      queryKeys.documentFlags.lists(),
      queryKeys.documents.all,
      queryKeys.documentRows.lists(),
    ],
    ...flagDeleteMutateOptions,
    onSettled: () => setIsDismissing(false),
  })

  return (
    <FlagListItemBase
      flag={flag}
      dismissFlag={() => {
        setIsDismissing(true)
        deleteDocumentFlag(flag.id)
      }}
      isDismissing={isDismissing}
    />
  )
}

export function RowValueFlagListItem({
  flag,
  onFlagClick,
}: RowValueFlagListItemProps) {
  const [isDismissing, setIsDismissing] = useState(false)

  const { deleteRowValueFlag } = useDeleteRowValueFlag({
    sideEffectQueryKeys: [
      queryKeys.rowValueFlags.lists(),
      queryKeys.documents.lists(),
      queryKeys.documentRows.lists(),
      queryKeys.documentRowValues.lists(),
    ],
    ...flagDeleteMutateOptions,
    onSettled: () => setIsDismissing(false),
  })

  const handleOnClick = () => {
    const cellToFocus = focusOnField(flag.document_row_value_id)

    onFlagClick?.(flag, cellToFocus)
  }

  return (
    <FlagListItemBase
      flag={flag}
      onClick={onFlagClick ? handleOnClick : undefined}
      dismissFlag={() => {
        setIsDismissing(true)
        deleteRowValueFlag(flag.id)
      }}
      isDismissing={isDismissing}
    />
  )
}
